import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { ArrowLeft, Building2, ChevronRight, Star, ImageOff, CheckCircle, XCircle, Clock, ExternalLink, Copy, Edit, Trash2, Calendar, Layers, AlertTriangle, Home, Tag, Users, MessageSquare, Flag, ArrowRight, ChevronDown, Send, Maximize2, X, Image as ImageIcon } from 'lucide-react';
import { getProductionRecord, getStageConfig, getMemberById, productionStages, productionPhaseConfig, teamRoleConfig, teamMembers, blockerCategoryConfig, type ProductionRecord, type ProductionStatus, type TeamMember } from '../../data/productionData';
import { statusConfig, allOptions } from './AdminOptionsPage';
import { builderColors } from './AdminProductionPage';
import { motion, AnimatePresence } from 'framer-motion';
function ThreeViewer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    let animationFrameId: number;
    let cleanup: (() => void) | null = null;
    import('three').then(THREE => {
      if (cancelled || !containerRef.current) return;
      const container = containerRef.current;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color('#f8fafc');
      const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
      camera.position.set(4, 3, 6);
      const renderer = new THREE.WebGLRenderer({
        antialias: true
      });
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.shadowMap.enabled = true;
      container.appendChild(renderer.domElement);
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      scene.add(ambientLight);
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
      dirLight.position.set(5, 10, 7);
      dirLight.castShadow = true;
      scene.add(dirLight);
      const group = new THREE.Group();
      scene.add(group);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0xf1f5f9,
        roughness: 0.2,
        metalness: 0.1
      });
      const handleMat = new THREE.MeshStandardMaterial({
        color: 0x94a3b8,
        roughness: 0.4,
        metalness: 0.8
      });
      const darkMat = new THREE.MeshStandardMaterial({
        color: 0x334155
      });
      const bodyGeo = new THREE.BoxGeometry(2, 4, 1.8);
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 2;
      body.castShadow = true;
      body.receiveShadow = true;
      group.add(body);
      const topDoorGeo = new THREE.BoxGeometry(2, 2.5, 0.2);
      const topDoor = new THREE.Mesh(topDoorGeo, bodyMat);
      topDoor.position.set(0, 2.75, 1.0);
      topDoor.castShadow = true;
      group.add(topDoor);
      const bottomDoorGeo = new THREE.BoxGeometry(2, 1.4, 0.2);
      const bottomDoor = new THREE.Mesh(bottomDoorGeo, bodyMat);
      bottomDoor.position.set(0, 0.75, 1.0);
      bottomDoor.castShadow = true;
      group.add(bottomDoor);
      const gapGeo = new THREE.BoxGeometry(1.9, 0.05, 0.2);
      const gap = new THREE.Mesh(gapGeo, darkMat);
      gap.position.set(0, 1.475, 1.0);
      group.add(gap);
      const handleGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.8);
      const topHandle = new THREE.Mesh(handleGeo, handleMat);
      topHandle.position.set(-0.7, 2.5, 1.15);
      topHandle.castShadow = true;
      group.add(topHandle);
      const bottomHandle = new THREE.Mesh(handleGeo, handleMat);
      bottomHandle.position.set(-0.7, 1.0, 1.15);
      bottomHandle.castShadow = true;
      group.add(bottomHandle);
      const gridHelper = new THREE.GridHelper(10, 10, 0xcbd5e1, 0xe2e8f0);
      scene.add(gridHelper);
      let isDragging = false;
      let prevMouse = {
        x: 0,
        y: 0
      };
      let autoRotate = true;
      const onDown = (e: MouseEvent) => {
        isDragging = true;
        autoRotate = false;
        prevMouse = {
          x: e.offsetX,
          y: e.offsetY
        };
      };
      const onMove = (e: MouseEvent) => {
        if (!isDragging) return;
        group.rotation.y += (e.offsetX - prevMouse.x) * 0.01;
        group.rotation.x += (e.offsetY - prevMouse.y) * 0.01;
        group.rotation.x = Math.max(-Math.PI / 4, Math.min(Math.PI / 4, group.rotation.x));
        prevMouse = {
          x: e.offsetX,
          y: e.offsetY
        };
      };
      const onUp = () => {
        isDragging = false;
      };
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        camera.position.z = Math.max(3, Math.min(15, camera.position.z + e.deltaY * 0.01));
      };
      const onResize = () => {
        if (!container) return;
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
      };
      const el = renderer.domElement;
      el.addEventListener('mousedown', onDown);
      el.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      el.addEventListener('wheel', onWheel, {
        passive: false
      });
      window.addEventListener('resize', onResize);
      camera.lookAt(0, 2, 0);
      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);
        if (autoRotate) group.rotation.y += 0.005;
        renderer.render(scene, camera);
      };
      animate();
      cleanup = () => {
        cancelAnimationFrame(animationFrameId);
        el.removeEventListener('mousedown', onDown);
        el.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        el.removeEventListener('wheel', onWheel);
        window.removeEventListener('resize', onResize);
        bodyGeo.dispose();
        topDoorGeo.dispose();
        bottomDoorGeo.dispose();
        gapGeo.dispose();
        handleGeo.dispose();
        bodyMat.dispose();
        handleMat.dispose();
        darkMat.dispose();
        renderer.dispose();
        if (container.contains(el)) container.removeChild(el);
      };
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    });
    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);
  if (loadError) {
    return <div className="w-full h-full flex items-center justify-center bg-slate-50 rounded-b-xl" data-id="element-1272">
        <p className="text-sm text-slate-400" data-id="element-1273">3D preview unavailable</p>
      </div>;
  }
  return <div className="w-full h-full relative bg-slate-50 rounded-b-xl overflow-hidden" data-id="element-1274">
      <div ref={containerRef} className="w-full h-full cursor-grab active:cursor-grabbing" data-id="element-1275" />
      <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none" data-id="element-1276">
        <span className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-slate-600 shadow-sm" data-id="element-1277">
          Drag to rotate • Scroll to zoom
        </span>
      </div>
    </div>;
}
export function AdminOptionDetailPage() {
  const {
    optionId
  } = useParams<{
    optionId: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([{
    id: 1,
    authorId: 'sarah-chen',
    timestamp: '2 days ago',
    text: 'Specs confirmed with builder. Moving to production next week.'
  }, {
    id: 2,
    authorId: 'jordan-lee',
    timestamp: '1 day ago',
    text: 'Started working on the 3D model. Need higher-res texture files.'
  }, {
    id: 3,
    authorId: 'sarah-chen',
    timestamp: '4 hours ago',
    text: 'Uploaded new texture files to the shared drive. @Jordan please check.'
  }]);
  // Use matched option or fall back to first option so every route shows a fully populated detail page
  const option = allOptions.find(o => o.id === optionId) || allOptions[0];
  const productionRecord = getProductionRecord(option.id) || getProductionRecord('OPT-001');
  const [currentAssigneeId, setCurrentAssigneeId] = useState<string | null>(productionRecord?.assignments.pm || null);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<'image' | '3d'>('image');
  // Handle escape key for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPreviewOpen) {
        setIsPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreviewOpen]);
  if (!option) {
    return <div className="max-w-5xl mx-auto space-y-6" data-id="element-1278">
        <div className="text-center py-12" data-id="element-1279">
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1280">
            Option not found
          </h2>
          <p className="text-slate-500" data-id="element-1281">
            The requested option does not exist or is not accessible.
          </p>
        </div>
      </div>;
  }
  const StatusIcon = statusConfig[option.status].icon;
  const communityPct = option.totalCommunities > 0 ? Math.round(option.communitiesAssigned / option.totalCommunities * 100) : 0;
  const modelPct = option.totalModels > 0 ? Math.round(option.modelsAssigned / option.totalModels * 100) : 0;
  const prodStage = productionRecord ? getStageConfig(productionRecord.productionStatus) : null;
  const stageIndex = productionRecord ? productionStages.findIndex(s => s.key === productionRecord.productionStatus) : -1;
  return <div className="max-w-5xl mx-auto space-y-6" data-id="element-1282">
      {/* Back nav */}
      <Button variant="ghost" onClick={() => navigate('/admin/options')} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-1284" />} data-id="element-1283">
        Back to Options
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between gap-6" data-id="element-1285">
        <div className="flex items-start gap-5" data-id="element-1286">
          <div className="h-20 w-20 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 relative group cursor-pointer" onClick={() => setIsPreviewOpen(true)} data-id="element-1287">
            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110" data-id="element-1288" /> : <ImageOff className="h-8 w-8 text-slate-300 transition-transform duration-300 group-hover:scale-110" data-id="element-1289" />}
            <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/20 transition-colors flex items-center justify-center" data-id="element-1290">
              <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" data-id="element-1291" />
            </div>
          </div>
          <div data-id="element-1292">
            <div className="flex items-center gap-3 mb-1" data-id="element-1293">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-1294">
                {option.name}
              </h1>
              {option.isStandard && <Badge variant="success" data-id="element-1295">
                  <Star className="h-3 w-3 mr-1" data-id="element-1296" /> Standard
                </Badge>}
            </div>
            <p className="text-sm text-slate-500 mb-2" data-id="element-1297">{option.id}</p>
            <div className="flex items-center gap-3" data-id="element-1298">
              <Badge variant={statusConfig[option.status].variant} data-id="element-1299">
                <StatusIcon className="h-3 w-3 mr-1" data-id="element-1300" />
                {statusConfig[option.status].label}
              </Badge>
              <div className="flex items-center gap-1.5 text-sm text-slate-500" data-id="element-1301">
                <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold ${builderColors[option.builderId] || 'bg-slate-100 text-slate-600'}`} data-id="element-1302">
                  {option.builderName.charAt(0)}
                </div>
                {option.builderName}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1303">
          <Button variant="outline" leftIcon={<ExternalLink className="h-4 w-4" data-id="element-1305" />} onClick={() => {
          // Map admin option IDs to builder option IDs
          const adminToBuilderMap: Record<string, string> = {
            'OPT-001': 'ext-1',
            'OPT-002': 'ext-2',
            'OPT-003': 'int-1',
            'OPT-004': 'int-8',
            'OPT-005': 'int-6',
            'OPT-006': 'int-7',
            'OPT-019': 'model-1'
          };
          const builderOptionId = adminToBuilderMap[option.id];
          if (builderOptionId) {
            navigate(`/${option.builderId}/options/${builderOptionId}`);
          } else {
            navigate(`/${option.builderId}/options`);
          }
        }} data-id="element-1304">
            Open in Builder
          </Button>
          <Button variant="outline" leftIcon={<Copy className="h-4 w-4" data-id="element-1307" />} data-id="element-1306">
            Duplicate
          </Button>
          <Button leftIcon={<Edit className="h-4 w-4" data-id="element-1309" />} data-id="element-1308">Edit Option</Button>
        </div>
      </div>

      {/* 3D Asset Preview — Hero (only for OPT-001 demo) */}
      {option.id === 'OPT-001' && <Card className="overflow-hidden border-slate-200 shadow-sm" noPadding data-id="element-1310">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-100" data-id="element-1311">
            <div className="flex items-center gap-2" data-id="element-1312">
              <div className="h-4 w-4 text-blue-600" data-id="element-1313" />
              <span className="text-sm font-semibold text-slate-900" data-id="element-1314">
                3D Asset Preview
              </span>
              <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-1315">
                Interactive
              </span>
            </div>
            <span className="text-xs text-slate-400" data-id="element-1316">
              Drag to rotate • Scroll to zoom
            </span>
          </div>
          <div className="h-[400px]" data-id="element-1317">
            <ThreeViewer data-id="element-1318" />
          </div>
        </Card>}

      {/* Main content grid */}
      <div className="grid grid-cols-3 gap-6" data-id="element-1319">
        {/* Left column — 2/3 */}
        <div className="col-span-2 space-y-6" data-id="element-1320">
          {/* Description */}
          <Card data-id="element-1321">
            <CardHeader data-id="element-1322">
              <CardTitle data-id="element-1323">Description</CardTitle>
            </CardHeader>
            <CardContent data-id="element-1324">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-1325">
                {option.description}
              </p>
            </CardContent>
          </Card>

          {/* Category & Classification */}
          <Card data-id="element-1326">
            <CardHeader data-id="element-1327">
              <CardTitle data-id="element-1328">Category & Classification</CardTitle>
            </CardHeader>
            <CardContent data-id="element-1329">
              <div className="flex items-center gap-2 text-sm mb-4" data-id="element-1330">
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-1331">
                  {option.category}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1332" />
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-1333">
                  {option.subcategory}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1334" />
                <span className="px-2.5 py-1 bg-slate-100 rounded-md font-medium text-slate-700" data-id="element-1335">
                  {option.variantSet}
                </span>
              </div>
              {option.tags.length > 0 && <div data-id="element-1336">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-1337">
                    Tags
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-1338">
                    {option.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-md" data-id="element-1339">
                        <Tag className="h-3 w-3" data-id="element-1340" />
                        {tag}
                      </span>)}
                  </div>
                </div>}
            </CardContent>
          </Card>

          {/* Production Panel */}
          {productionRecord && prodStage && <Card className="border-blue-200 shadow-sm overflow-hidden" noPadding data-id="element-1341">
              <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-1342">
                <div className="flex items-center gap-2" data-id="element-1343">
                  <Layers className="h-5 w-5 text-blue-600" data-id="element-1344" />
                  <h2 className="text-lg font-semibold text-slate-900" data-id="element-1345">
                    Production Status
                  </h2>
                </div>
                <div className="flex items-center gap-3" data-id="element-1346">
                  {/* Status Dropdown */}
                  <div className="relative" data-id="element-1347">
                    <select value={productionRecord.productionStatus} onChange={e => console.log('Change status to', e.target.value)} className={`appearance-none pl-3 pr-8 py-1.5 text-sm font-medium rounded-md border ${prodStage.bgColor} ${prodStage.color} ${prodStage.borderColor} focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer`} data-id="element-1348">
                      {Object.entries(productionPhaseConfig).map(([phaseKey, phase]) => <optgroup key={phaseKey} label={phase.label} data-id="element-1349">
                            {productionStages.filter(s => s.phase === phaseKey).map(s => <option key={s.key} value={s.key} data-id="element-1350">
                                  {s.label}
                                </option>)}
                          </optgroup>)}
                    </select>
                    <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 ${prodStage.color} pointer-events-none`} data-id="element-1351" />
                  </div>
                  {/* QA Level Selector */}
                  {productionRecord.productionStatus === 'internal-qa' && <div className="flex items-center gap-1.5" data-id="element-1352">
                      <span className="text-xs font-medium text-slate-500" data-id="element-1353">
                        Round
                      </span>
                      <div className="flex items-center gap-0.5 bg-violet-50 border border-violet-200 rounded-lg p-0.5" data-id="element-1354">
                        {[1, 2, 3].map(level => <button key={level} onClick={() => console.log('Set QA level to', level)} className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${(productionRecord.qaLevel || 1) === level ? 'bg-violet-600 text-white shadow-sm' : 'text-violet-600 hover:bg-violet-100'}`} data-id="element-1355">
                            {level}
                          </button>)}
                      </div>
                    </div>}
                </div>
              </div>

              <div className="p-0" data-id="element-1356">
                {/* Stepper */}
                <div className="px-6 py-8 border-b border-slate-100 bg-white" data-id="element-1357">
                  <div className="flex items-center justify-between relative px-2" data-id="element-1358">
                    <div className="absolute left-2 right-2 top-1/2 -translate-y-1/2 h-1 bg-slate-100 rounded-full" data-id="element-1359" />
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full transition-all" style={{
                  width: `calc(${stageIndex / (productionStages.length - 1) * 100}% - 4px)`
                }} data-id="element-1360" />
                    {productionStages.map((stage, i) => {
                  const isCompleted = i <= stageIndex;
                  const isCurrent = i === stageIndex;
                  return <div key={stage.key} className="relative flex flex-col items-center group z-10" data-id="element-1361">
                          <div className={`w-3 h-3 rounded-full border-2 ${isCurrent ? 'bg-white border-blue-600 ring-4 ring-blue-100' : isCompleted ? 'bg-blue-500 border-blue-500' : 'bg-white border-slate-300'}`} title={stage.label} data-id="element-1362" />
                          {isCurrent && <span className="absolute top-5 text-[10px] font-bold text-blue-700 whitespace-nowrap bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100" data-id="element-1363">
                              {stage.shortLabel}
                            </span>}
                        </div>;
                })}
                  </div>
                </div>

                <div className="grid grid-cols-2 divide-x divide-slate-100" data-id="element-1364">
                  {/* Left side: Details */}
                  <div className="p-6 space-y-6" data-id="element-1365">
                    {/* Blockers */}
                    {productionRecord.blockers.length > 0 && <div className="space-y-3" data-id="element-1366">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1367">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500" data-id="element-1368" />{' '}
                          Blockers
                        </h3>
                        <div className="space-y-2" data-id="element-1369">
                          {productionRecord.blockers.map((blocker, i) => <div key={i} className="bg-red-50 border border-red-100 rounded-md p-3" data-id="element-1370">
                              <div className="flex items-center gap-2 mb-1" data-id="element-1371">
                                <Badge variant="error" className="text-[10px] px-1.5 py-0" data-id="element-1372">
                                  {blockerCategoryConfig[blocker.category]?.label || blocker.category}
                                </Badge>
                                <span className="text-[10px] text-red-400" data-id="element-1373">
                                  {blocker.createdAt}
                                </span>
                              </div>
                              {blocker.note && <p className="text-xs text-red-800 mt-1.5" data-id="element-1374">
                                  {blocker.note}
                                </p>}
                            </div>)}
                        </div>
                      </div>}

                    {/* Notes */}
                    <div className="space-y-2" data-id="element-1375">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1376">
                        <MessageSquare className="h-3.5 w-3.5" data-id="element-1377" /> Production
                        Notes
                      </h3>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-md border border-slate-100" data-id="element-1378">
                        {productionRecord.notes || 'No production notes added yet.'}
                      </p>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4" data-id="element-1379">
                      <div data-id="element-1380">
                        <p className="text-xs text-slate-500 mb-1" data-id="element-1381">
                          Start Date
                        </p>
                        <p className="text-sm font-medium text-slate-900" data-id="element-1382">
                          {productionRecord.startDate || '—'}
                        </p>
                      </div>
                      <div data-id="element-1383">
                        <p className="text-xs text-slate-500 mb-1" data-id="element-1384">
                          Target Date
                        </p>
                        <p className="text-sm font-medium text-slate-900" data-id="element-1385">
                          {productionRecord.targetDate || '—'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right side: Current Owner & History */}
                  <div className="p-6 space-y-6 bg-slate-50/50" data-id="element-1386">
                    {/* Current Owner */}
                    <div className="space-y-3" data-id="element-1387">
                      <div className="flex items-center justify-between" data-id="element-1388">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1389">
                          <Users className="h-3.5 w-3.5" data-id="element-1390" /> Current Owner
                        </h3>
                        <div className="relative" data-id="element-1391">
                          <Button variant="ghost" size="sm" onClick={() => setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)} className="h-6 text-xs px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-id="element-1392">
                            Reassign
                          </Button>
                          {isAssigneeDropdownOpen && <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 z-50 py-1" data-id="element-1393">
                              <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/50" data-id="element-1394">
                                <p className="text-xs font-medium text-slate-500" data-id="element-1395">
                                  Select Assignee
                                </p>
                              </div>
                              <div className="max-h-64 overflow-y-auto py-1" data-id="element-1396">
                                {Object.entries(teamRoleConfig).map(([roleKey, roleConfig]) => {
                            const roleMembers = teamMembers.filter(m => m.role === roleKey);
                            if (roleMembers.length === 0) return null;
                            return <div key={roleKey} className="mb-2 last:mb-0" data-id="element-1397">
                                        <div className="px-3 py-1" data-id="element-1398">
                                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-1399">
                                            {roleConfig.label}
                                          </span>
                                        </div>
                                        {roleMembers.map(member => <button key={member.id} onClick={() => {
                                setCurrentAssigneeId(member.id);
                                setIsAssigneeDropdownOpen(false);
                              }} className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 transition-colors ${currentAssigneeId === member.id ? 'bg-blue-50/50' : ''}`} data-id="element-1400">
                                            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${member.avatarColor}`} data-id="element-1401">
                                              {member.initials}
                                            </div>
                                            <span className={`text-sm ${currentAssigneeId === member.id ? 'font-medium text-blue-700' : 'text-slate-700'}`} data-id="element-1402">
                                              {member.name}
                                            </span>
                                            {currentAssigneeId === member.id && <CheckCircle className="h-3.5 w-3.5 text-blue-600 ml-auto" data-id="element-1403" />}
                                          </button>)}
                                      </div>;
                          })}
                              </div>
                            </div>}
                        </div>
                      </div>
                      {(() => {
                    const currentOwner = currentAssigneeId ? getMemberById(currentAssigneeId) : null;
                    const ownerRole = currentOwner ? teamRoleConfig[currentOwner.role as keyof typeof teamRoleConfig] : null;
                    if (currentOwner && ownerRole) {
                      return <div className="flex items-center justify-between bg-white p-3 rounded-md border border-slate-200" data-id="element-1404">
                              <div className="flex items-center gap-3" data-id="element-1405">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${currentOwner.avatarColor}`} data-id="element-1406">
                                  {currentOwner.initials}
                                </div>
                                <div data-id="element-1407">
                                  <span className="text-sm font-medium text-slate-900" data-id="element-1408">
                                    {currentOwner.name}
                                  </span>
                                  <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-1409">
                                    Manually assigned
                                  </p>
                                </div>
                              </div>
                              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ownerRole.bgColor} ${ownerRole.color}`} data-id="element-1410">
                                {ownerRole.shortLabel}
                              </span>
                            </div>;
                    }
                    return <div className="bg-slate-50 border border-slate-200 border-dashed rounded-md p-3 text-center" data-id="element-1411">
                            <p className="text-sm text-slate-500 italic" data-id="element-1412">
                              No owner assigned.
                            </p>
                            <p className="text-xs text-slate-400 mt-1" data-id="element-1413">
                              Click Reassign to set an owner.
                            </p>
                          </div>;
                  })()}
                    </div>
                    {/* Stage History */}
                    <div className="space-y-3" data-id="element-1414">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1415">
                        <Flag className="h-3.5 w-3.5" data-id="element-1416" /> Stage History
                      </h3>
                      {productionRecord.history.length > 0 ? <div className="space-y-2" data-id="element-1417">
                          {productionRecord.history.map((transition, i) => {
                      const fromStage = productionStages.find(s => s.key === transition.from);
                      const toStage = productionStages.find(s => s.key === transition.to);
                      return <div key={i} className="flex items-center gap-2 text-xs" data-id="element-1418">
                                <span className="text-slate-400 w-20 flex-shrink-0" data-id="element-1419">
                                  {transition.date}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fromStage?.bgColor || 'bg-slate-100'} ${fromStage?.color || 'text-slate-600'}`} data-id="element-1420">
                                  {fromStage?.shortLabel || transition.from}
                                </span>
                                <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" data-id="element-1421" />
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${toStage?.bgColor || 'bg-slate-100'} ${toStage?.color || 'text-slate-600'}`} data-id="element-1422">
                                  {toStage?.shortLabel || transition.to}
                                </span>
                                <span className="text-slate-400 ml-auto" data-id="element-1423">
                                  {transition.user}
                                </span>
                              </div>;
                    })}
                        </div> : <p className="text-xs text-slate-400 italic border-l-2 border-slate-200 pl-3" data-id="element-1424">
                          No stage transitions yet.
                        </p>}
                    </div>
                  </div>
                </div>
              </div>
            </Card>}

          {/* Comments & Activity */}
          <Card data-id="element-1425">
            <CardHeader className="border-b border-slate-100 pb-4" data-id="element-1426">
              <div className="flex items-center gap-2" data-id="element-1427">
                <MessageSquare className="h-5 w-5 text-slate-400" data-id="element-1428" />
                <CardTitle data-id="element-1429">Comments & Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6" data-id="element-1430">
              <div className="space-y-6" data-id="element-1431">
                {comments.map(comment => {
                const author = getMemberById(comment.authorId);
                if (!author) return null;
                const roleConfig = teamRoleConfig[author.role as keyof typeof teamRoleConfig];
                return <div key={comment.id} className="flex gap-4" data-id="element-1432">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-1 ${author.avatarColor}`} data-id="element-1433">
                        {author.initials}
                      </div>
                      <div className="flex-1 space-y-1" data-id="element-1434">
                        <div className="flex items-center gap-2" data-id="element-1435">
                          <span className="text-sm font-medium text-slate-900" data-id="element-1436">
                            {author.name}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${roleConfig.bgColor} ${roleConfig.color}`} data-id="element-1437">
                            {roleConfig.shortLabel}
                          </span>
                          <span className="text-xs text-slate-400 ml-auto" data-id="element-1438">
                            {comment.timestamp}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-1439">
                          {comment.text}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              <div className="flex gap-4 pt-4 border-t border-slate-100" data-id="element-1440">
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-slate-800 flex-shrink-0" data-id="element-1441">
                  JD
                </div>
                <div className="flex-1 space-y-3" data-id="element-1442">
                  <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment or update..." className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none min-h-[80px]" data-id="element-1443" />
                  <div className="flex justify-end" data-id="element-1444">
                    <Button size="sm" disabled={!newComment.trim()} onClick={() => {
                    if (newComment.trim()) {
                      setComments([...comments, {
                        id: Date.now(),
                        authorId: 'sarah-chen',
                        timestamp: 'Just now',
                        text: newComment.trim()
                      }]);
                      setNewComment('');
                    }
                  }} leftIcon={<Send className="h-3.5 w-3.5" data-id="element-1446" />} data-id="element-1445">
                      Post Comment
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card data-id="element-1447">
            <CardHeader data-id="element-1448">
              <CardTitle data-id="element-1449">Assigned To</CardTitle>
            </CardHeader>
            <CardContent data-id="element-1450">
              <div className="space-y-4" data-id="element-1451">
                {Object.entries(teamRoleConfig).map(([roleKey, roleConfig]) => {
                const assignedMemberId = productionRecord?.assignments[roleKey as keyof typeof productionRecord.assignments];
                const member = assignedMemberId ? getMemberById(assignedMemberId) : null;
                return <div key={roleKey} className="flex items-center justify-between" data-id="element-1452">
                      <div className="flex items-center gap-3" data-id="element-1453">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${member ? member.avatarColor : 'bg-slate-200 text-slate-400'}`} data-id="element-1454">
                          {member ? member.initials : '?'}
                        </div>
                        <div data-id="element-1455">
                          <p className="text-sm font-medium text-slate-900" data-id="element-1456">
                            {member ? member.name : 'Unassigned'}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-1457">
                            {roleConfig.label}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" data-id="element-1458">
                        {member ? 'Change' : 'Assign'}
                      </Button>
                    </div>;
              })}
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Right column — 1/3 */}
        {/* MASKED_CODE_RIGHT_COLUMN_PLACEHOLDER */}
      </div>

      {/* Asset Preview Modal */}
      <AnimatePresence data-id="element-1459">
        {isPreviewOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-12" data-id="element-1460">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsPreviewOpen(false)} data-id="element-1461" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative w-full max-w-5xl h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden z-10" data-id="element-1462">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-1463">
                <div data-id="element-1464">
                  <h2 className="text-lg font-bold text-slate-900" data-id="element-1465">
                    {option.name}
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-1466">
                    {option.id} • {option.category}
                  </p>
                </div>
                <button onClick={() => setIsPreviewOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-1467">
                  <X className="h-5 w-5" data-id="element-1468" />
                </button>
              </div>

              {/* Modal Content — Image Only */}
              <div className="flex-1 bg-slate-50 relative" data-id="element-1469">
                <div className="absolute inset-0 flex items-center justify-center p-8" data-id="element-1470">
                  {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="max-w-full max-h-full object-contain rounded-lg shadow-sm border border-slate-200 bg-white" data-id="element-1471" /> : <div className="flex flex-col items-center text-slate-400" data-id="element-1472">
                      <ImageOff className="h-24 w-24 mb-4 opacity-50" data-id="element-1473" />
                      <p className="text-lg font-medium" data-id="element-1474">No image available</p>
                    </div>}
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}